import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Eye, Clock, User, Check, Building2, Phone, Mail, X, MessageSquare, Send } from 'lucide-react';
import { devisAPI, messagesAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const AdminDevis = () => {
  const [devisList, setDevisList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDevis, setSelectedDevis] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadDevis();
  }, []);

  const loadDevis = async () => {
    try {
      const res = await devisAPI.list();
      setDevisList(res.data);
    } catch (error) {
      console.error('Error loading devis:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await devisAPI.updateStatus(id, newStatus);
      setDevisList(devisList.map(d => d.id === id ? { ...d, status: newStatus } : d));
      if (selectedDevis?.id === id) {
        setSelectedDevis({ ...selectedDevis, status: newStatus });
      }
      toast({ title: 'Statut mis à jour' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de mettre à jour', variant: 'destructive' });
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;
    
    setSending(true);
    try {
      // Envoyer via la messagerie interne (créer un utilisateur temporaire si nécessaire)
      // Pour l'instant, on peut juste marquer comme traité
      await updateStatus(selectedDevis.id, 'traite');
      toast({ title: 'Réponse envoyée', description: 'Le devis a été marqué comme traité' });
      setShowReplyModal(false);
      setReplyMessage('');
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible d\'envoyer la réponse', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      nouveau: 'bg-blue-100 text-blue-800',
      en_cours: 'bg-yellow-100 text-yellow-800',
      traite: 'bg-green-100 text-green-800',
      refuse: 'bg-red-100 text-red-800',
    };
    const labels = {
      nouveau: 'Nouveau',
      en_cours: 'En cours',
      traite: 'Traité',
      refuse: 'Refusé',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const isEntreprise = type === 'entreprise';
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
        isEntreprise ? 'bg-purple-100 text-purple-800' : 'bg-teal-100 text-teal-800'
      }`}>
        {isEntreprise ? 'Entreprise' : 'Particulier'}
      </span>
    );
  };

  const newCount = devisList.filter(d => d.status === 'nouveau').length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-devis">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-[#2C3C65]">Demandes de devis</h1>
            {newCount > 0 && (
              <p className="text-sm text-[#6777A1] mt-1">{newCount} nouvelle(s) demande(s)</p>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Liste des devis */}
          <div className="lg:col-span-1 bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h2 className="font-semibold text-[#2C3C65]">Demandes ({devisList.length})</h2>
            </div>
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB] mx-auto"></div>
              </div>
            ) : devisList.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-[#6777A1]">Aucune demande</p>
              </div>
            ) : (
              <div className="divide-y max-h-[600px] overflow-y-auto">
                {devisList.map((devis) => (
                  <motion.div
                    key={devis.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => setSelectedDevis(devis)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedDevis?.id === devis.id ? 'bg-blue-50' : ''
                    } ${devis.status === 'nouveau' ? 'bg-blue-50/50' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getTypeBadge(devis.type)}
                        {getStatusBadge(devis.status)}
                      </div>
                    </div>
                    <p className="font-medium text-[#2C3C65]">
                      {devis.prenom} {devis.nom}
                    </p>
                    {devis.entreprise && (
                      <p className="text-sm text-[#6777A1] flex items-center gap-1 mt-1">
                        <Building2 className="h-3 w-3" />
                        {devis.entreprise}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-2">
                      {new Date(devis.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Détail du devis */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-md">
            {selectedDevis ? (
              <div className="p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      {getTypeBadge(selectedDevis.type)}
                      {getStatusBadge(selectedDevis.status)}
                    </div>
                    <h3 className="text-xl font-semibold text-[#2C3C65]">
                      {selectedDevis.prenom} {selectedDevis.nom}
                    </h3>
                    {selectedDevis.entreprise && (
                      <p className="text-[#6777A1] flex items-center gap-2 mt-1">
                        <Building2 className="h-4 w-4" />
                        {selectedDevis.entreprise}
                        {selectedDevis.fonction && ` - ${selectedDevis.fonction}`}
                      </p>
                    )}
                  </div>
                  <div className="text-right text-sm text-gray-400">
                    <Clock className="h-4 w-4 inline mr-1" />
                    {new Date(selectedDevis.created_at).toLocaleString('fr-FR')}
                  </div>
                </div>

                {/* Coordonnées */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Email</p>
                    <a href={`mailto:${selectedDevis.email}`} className="text-[#285BBB] hover:underline flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {selectedDevis.email}
                    </a>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Téléphone</p>
                    <a href={`tel:${selectedDevis.telephone}`} className="text-[#285BBB] hover:underline flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {selectedDevis.telephone}
                    </a>
                  </div>
                </div>

                {/* Détails de la demande */}
                <div className="space-y-4 mb-6">
                  <h4 className="font-semibold text-[#2C3C65]">Détails de la demande</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-blue-600 mb-1">Nombre de personnes</p>
                      <p className="font-semibold text-[#2C3C65]">{selectedDevis.nombre_personnes || '1'}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-blue-600 mb-1">Niveau actuel</p>
                      <p className="font-semibold text-[#2C3C65]">{selectedDevis.niveau_actuel || 'Non précisé'}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-blue-600 mb-1">Financement</p>
                      <p className="font-semibold text-[#2C3C65]">{selectedDevis.financement === 'cpf' ? 'CPF' : selectedDevis.financement || 'Non précisé'}</p>
                    </div>
                  </div>
                  
                  {selectedDevis.objectif && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 mb-2">Objectif</p>
                      <p className="text-[#2C3C65]">{selectedDevis.objectif}</p>
                    </div>
                  )}
                  
                  {selectedDevis.disponibilites && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 mb-2">Disponibilités</p>
                      <p className="text-[#2C3C65]">{selectedDevis.disponibilites}</p>
                    </div>
                  )}
                  
                  {selectedDevis.message && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 mb-2">Message</p>
                      <p className="text-[#2C3C65] whitespace-pre-wrap">{selectedDevis.message}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-4 border-t">
                  <Button
                    onClick={() => setShowReplyModal(true)}
                    className="bg-[#285BBB] hover:bg-[#2C3C65]"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Répondre
                  </Button>
                  {selectedDevis.status === 'nouveau' && (
                    <Button
                      variant="outline"
                      onClick={() => updateStatus(selectedDevis.id, 'en_cours')}
                    >
                      Marquer en cours
                    </Button>
                  )}
                  {selectedDevis.status !== 'traite' && (
                    <Button
                      variant="outline"
                      className="text-green-600 border-green-300 hover:bg-green-50"
                      onClick={() => updateStatus(selectedDevis.id, 'traite')}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Marquer comme traité
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="text-center">
                  <FileText className="h-16 w-16 text-gray-200 mx-auto mb-4" />
                  <p className="text-[#6777A1]">Sélectionnez une demande pour voir les détails</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de réponse */}
      <AnimatePresence>
        {showReplyModal && selectedDevis && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowReplyModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-bold text-[#2C3C65]">Répondre à {selectedDevis.prenom} {selectedDevis.nom}</h3>
                <button onClick={() => setShowReplyModal(false)}>
                  <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              </div>
              <div className="p-4">
                <p className="text-sm text-[#6777A1] mb-4">
                  Votre réponse sera envoyée par email à {selectedDevis.email}
                </p>
                <Textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Écrivez votre réponse..."
                  rows={6}
                  className="mb-4"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowReplyModal(false)}>
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSendReply}
                    disabled={sending || !replyMessage.trim()}
                    className="bg-[#285BBB] hover:bg-[#2C3C65]"
                  >
                    {sending ? 'Envoi...' : 'Envoyer'}
                    <Send className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

export default AdminDevis;
